import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { wxUtils } from "@features/wx/utils";
import { useJobCardMaterialReport } from "../../../hooks";
import { Checkbox } from "@shared/ui/checkbox";

const createPaginationConfig = (data: any[], page: number, perPage: number) => ({
  currentPage: page,
  totalPages: Math.ceil((data?.length || 0) / perPage),
  totalItems: data?.length || 0,
  itemsPerPage: perPage,
  startItem: (page - 1) * perPage + 1,
  endItem: Math.min(page * perPage, data?.length || 0),
});

const getPaginatedData = (data: any[], page: number, perPage: number) => {
  if (!data || data.length === 0) return [];
  const startIndex = (page - 1) * perPage;
  return data.slice(startIndex, startIndex + perPage);
};

export const MaterialsTab = ({
  jobCard,
  isReport,
  id,
  isHistory,
  isCompleted,
}: {
  jobCard: any;
  isReport?: boolean;
  id?: string;
  isHistory?: boolean;
  isCompleted?: boolean;
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const currency = useCurrency();

  const { data: materialReportData } = useJobCardMaterialReport(id, {
    enabled: !!isReport && !!id,
  });

  const transformedData = useMemo(
    () => jobCard && wxUtils.transformJobCardDetail(jobCard),
    [jobCard]
  );

  const reportMaterials = useMemo(() => {
    return (
      materialReportData?.result?.material?.map((material, index) => ({
        materialId: index,
        name: material.name || "N/A",
        size: material.size || "N/A",
        quantityRequired: material.quantityRequired || "0",
        quantityUsed: material.quantityUsed || "0",
        cost: material.cost || "0",
        unit: material.unit || "pcs",
      })) || []
    );
  }, [materialReportData]);

  const materialsData = isReport
    ? reportMaterials
    : transformedData?.materialsData || [];

  const materialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (v: string) => (
        <span className="font-medium text-gray-900">{v}</span>
      ),
    },
    { key: "size", header: "Size", render: (v: string) => <span>{v}</span> },
    ...(isReport
      ? [
          {
            key: "quantityRequired",
            header: "Est Quantity",
            render: (v: number) => <span>{v}</span>,
          },
          {
            key: "quantityUsed",
            header: "Actual Used Quantity",
            render: (v: number) => <span>{v}</span>,
          },
          {
            key: "cost",
            header: "Cost Per Unit",
            render: (v: any) => (
              <span className="font-semibold text-green-600">
                {currency} {v}
              </span>
            ),
          },
          {
            key: "action",
            header: "Action",
            render: () => (
              <div className="text-center">
                <Checkbox defaultChecked={!isCompleted} disabled={isHistory} />
              </div>
            ),
          },
        ]
      : [
          {
            key: "quantityRequired",
            header: "Quantity",
            render: (v: number) => <span>{v}</span>,
          },
          {
            key: "cost",
            header: "Cost",
            render: (v: any) => (
              <span className="font-semibold text-green-600">
                {currency} {parseFloat(v || 0).toFixed(2)}
              </span>
            ),
          },
        ]),
  ];



  return (
    <GenericTable
      data={getPaginatedData(materialsData, currentPage, itemsPerPage)}
      columns={materialsColumns}
      emptyMessage={`No materials found`}
      rowKey={"materialId"}
      pagination={createPaginationConfig(
        materialsData,
        currentPage,
        itemsPerPage
      )}
      onPageChange={setCurrentPage}
    />
  );
};

